import React from 'react';
import { Loader2, Heart, Users, Globe, BookOpen } from 'lucide-react';

const Loader = ({ 
  type = 'default', 
  size = 'medium', 
  text = 'Chargement...', 
  className = '' 
}) => {
  const sizeClasses = {
    small: 'h-6 w-6',
    medium: 'h-8 w-8',
    large: 'h-12 w-12',
    xlarge: 'h-16 w-16'
  };

  const textSizes = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg',
    xlarge: 'text-xl'
  };

  const renderIcon = () => {
    const iconClass = `${sizeClasses[size]} text-[#089556] animate-spin`;
    
    switch (type) {
      case 'heart':
        return <Heart className={iconClass} />;
      case 'users':
        return <Users className={iconClass} />;
      case 'globe':
        return <Globe className={iconClass} />;
      case 'book':
        return <BookOpen className={iconClass} />;
      default:
        return <Loader2 className={iconClass} />;
    }
  };

  return (
    <div className={`flex flex-col items-center justify-center p-8 ${className}`}>
      {renderIcon()}
      {text && (
        <p className={`mt-4 text-gray-600 font-medium ${textSizes[size]}`}>
          {text}
        </p>
      )}
    </div>
  );
};

// Variantes spécialisées
export const HeroLoader = () => (
  <div className="w-full py-8">
    <div className="max-w-7xl mx-auto px-4">
      <div className="flex flex-col lg:flex-row bg-white shadow-xl overflow-hidden ml-0 lg:ml-6 animate-pulse">
        {/* Image skeleton - Left Side */}
        <div className="lg:w-[68%] h-[320px] lg:h-[420px] bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-shimmer"></div>
        
        {/* Content skeleton - Right Side */}
        <div className="lg:w-[32%] p-5 lg:p-7 space-y-4">
          {/* Title skeleton */}
          <div className="space-y-2">
            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
            <div className="h-6 bg-gray-200 rounded w-full"></div>
          </div>
          
          {/* Description skeleton */}
          <div className="space-y-2 pt-2">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
          
          {/* Buttons skeleton */}
          <div className="space-y-2.5 pt-4">
            <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const CardLoader = ({ className = '' }) => (
  <div className={`bg-white rounded-xl overflow-hidden shadow-lg animate-pulse ${className}`}>
    <div className="h-48 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-shimmer"></div>
    <div className="p-6 space-y-4">
      <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-full"></div>
        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
      </div>
      <div className="h-10 bg-gray-200 rounded-lg w-full mt-4"></div>
    </div>
  </div>
);

export const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <Loader 
      type="globe" 
      size="xlarge" 
      text="Chargement de la page..."
      className="text-center"
    />
  </div>
);

export const FullPageLoader = () => (
  <div className="min-h-screen bg-white">
    {/* Hero Section Skeleton */}
    <section className="relative py-12 bg-gradient-to-br from-gray-200 via-gray-300 to-gray-200 overflow-hidden">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center animate-pulse">
          <div className="h-12 bg-gray-300 rounded-lg w-3/4 mx-auto mb-4"></div>
          <div className="h-8 bg-gray-300 rounded-lg w-1/2 mx-auto"></div>
        </div>
      </div>
    </section>

    {/* Content Section Skeleton */}
    <section className="py-12 bg-white">
      <div className="max-w-4xl mx-auto px-4 animate-pulse">
        {/* Image Skeleton */}
        <div className="mb-8">
          <div className="w-full h-96 bg-gray-200 rounded-xl shadow-lg"></div>
        </div>

        {/* Text Content Skeleton */}
        <div className="prose prose-lg max-w-none space-y-4">
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>

          <div className="space-y-3 pt-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>

          <div className="space-y-3 pt-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>

          <div className="space-y-3 pt-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/6"></div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Loader;

// Animation shimmer pour les loaders
const shimmerAnimation = `
  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
  
  .animate-shimmer {
    animation: shimmer 2s infinite linear;
  }
`;

// Injecter l'animation dans le DOM
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = shimmerAnimation;
  document.head.appendChild(style);
}
